import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["size", "variant", "aria-label", "xstyle", "className", "style"];
import { c as _c } from "react-compiler-runtime";
import { forwardRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import unescapedText from 'I18n/utils/unescapedText';
import { DATA_COMPONENT_ATTRIBUTE } from '../../constants/dataAttributes';
import { theme } from 'trellis-theming/modern-theme.stylex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const COMPONENT_NAME = 'Spinner';
const VIEWBOX_SIZE = 24;
const RADIUS = 10;
const STROKE_WIDTH = 2.5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const ARC_LENGTH = CIRCUMFERENCE * 0.25;
const spinKeyframes = "x1wc8ddo-B";
const rootStyles = {
  root: {
    alignItems: "x6s0dn4",
    display: "x3nfvp2",
    justifyContent: "xl56j7k",
    $$css: true
  },
  size12: {
    height: "x1kpxq89",
    width: "xsmyaan",
    $$css: true
  },
  size14: {
    height: "x1v9usgg",
    width: "x6jxa94",
    $$css: true
  },
  size16: {
    height: "xlup9mm",
    width: "x1kky2od",
    $$css: true
  },
  size20: {
    height: "x1qx5ct2",
    width: "xw4jnvo",
    $$css: true
  },
  size48: {
    height: "xsdox4t",
    width: "x1useyqa",
    $$css: true
  }
};
const trackStyles = {
  default: {
    color: "x1uzp0x1",
    $$css: true
  },
  inverse: {
    color: "x1t8b4hb",
    $$css: true
  }
};
const arcStyles = {
  default: {
    color: "x1pqkxhe",
    $$css: true
  },
  inverse: {
    color: "x69kfgm",
    $$css: true
  }
};
const SIZE_STYLE_MAP = {
  12: rootStyles.size12,
  14: rootStyles.size14,
  16: rootStyles.size16,
  20: rootStyles.size20,
  48: rootStyles.size48
};

/**
 * Displays an animated loading indicator to communicate that content is being loaded or a process is in progress.
 *
 * This component is part of Trellis's compositive API layer. Prefer the config
 * API, which covers the majority of use cases. Only reach for this component
 * when the config API can't express your pattern, and share your use case in
 * #trellis-support — it directly informs how we evolve the config layer.
 *
 * @see {@link __SpinnerConfig | Spinner}
 */
export const Spinner = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(31);
  let restProps;
  let t1;
  let t2;
  let t3;
  let xstyle;
  if ($[0] !== t0) {
    const {
        size: t4,
        variant: t5,
        "aria-label": t6,
        xstyle: t7
      } = t0,
      t8 = _objectWithoutPropertiesLoose(t0, _excluded);
    t1 = t4;
    t2 = t5;
    t3 = t6;
    xstyle = t7;
    restProps = t8;
    $[0] = t0;
    $[1] = restProps;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = xstyle;
  } else {
    restProps = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    xstyle = $[5];
  }
  const size = t1 === undefined ? 48 : t1;
  const variant = t2 === undefined ? "default" : t2;
  let t4;
  if ($[6] !== t3) {
    t4 = t3 === undefined ? unescapedText("trellis-components.spinner.ariaLabel") : t3;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const ariaLabel = t4;
  let t5;
  if ($[8] !== size || $[9] !== xstyle) {
    t5 = stylex.props(rootStyles.root, SIZE_STYLE_MAP[size], xstyle);
    $[8] = size;
    $[9] = xstyle;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      [DATA_COMPONENT_ATTRIBUTE]: COMPONENT_NAME
    };
    t7 = {
      className: "x7v9bd0 xa4qsjk x1aerksh xoj058f x1jf1jvw x1esw782 x5yr21d x1lliihq xh8yej3 x1bndym7 x1so62im"
    };
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      className: "x5yr21d x1lliihq xh8yej3"
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== variant) {
    t9 = stylex.props(trackStyles[variant]);
    $[14] = variant;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = /*#__PURE__*/_jsx("circle", Object.assign({
      cx: VIEWBOX_SIZE / 2,
      cy: VIEWBOX_SIZE / 2,
      r: RADIUS,
      strokeWidth: STROKE_WIDTH,
      fill: "none"
    }, t9, {
      stroke: "currentColor"
    }));
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== variant) {
    t11 = stylex.props(arcStyles[variant]);
    $[18] = variant;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = /*#__PURE__*/_jsx("circle", Object.assign({
      cx: VIEWBOX_SIZE / 2,
      cy: VIEWBOX_SIZE / 2,
      r: RADIUS,
      strokeWidth: STROKE_WIDTH,
      fill: "none",
      strokeDasharray: `${ARC_LENGTH} ${CIRCUMFERENCE}`,
      strokeLinecap: "round"
    }, t11, {
      stroke: "currentColor"
    }));
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12) {
    t13 = /*#__PURE__*/_jsx("div", Object.assign({}, t7, {
      children: /*#__PURE__*/_jsxs("svg", Object.assign({
        viewBox: `0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`,
        fill: "none",
        "aria-hidden": "true"
      }, t8, {
        children: [t10, t12]
      }))
    }));
    $[22] = t10;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== ariaLabel || $[26] !== ref || $[27] !== restProps || $[28] !== t13 || $[29] !== t5) {
    t14 = /*#__PURE__*/_jsx("div", Object.assign({}, restProps, {
      ref: ref,
      role: "status",
      "aria-label": ariaLabel
    }, t5, t6, {
      children: t13
    }));
    $[25] = ariaLabel;
    $[26] = ref;
    $[27] = restProps;
    $[28] = t13;
    $[29] = t5;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
});
Spinner.displayName = COMPONENT_NAME;